import React from 'react';
import VideoJS from '../components/video-js';

const videoJsOptions = (mediaURL, type = 'video/mp4') => (
  {
    autoplay: false,
    controls: true,
    responsive: true,
    fluid: true,
    sources: [{
      src: mediaURL,
      type,
    }],
  }
);

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];
const VIDEO_FILE_EXTENSIONS = ['mp4', 'm4v', 'mov', 'webm', 'ogv', 'm3u8'];

// Pull the file extension off the pathname only, ignoring any query string or
// hash (e.g. `.jpg?token=abc` still resolves to `jpg`). Returns '' when the URL
// carries no recognizable extension, which is common for hosted-player links
// such as Vimeo or YouTube.
const getUrlExtension = (mediaURL) => {
  let pathname = mediaURL;
  try {
    ({ pathname } = new URL(mediaURL, window.location.origin));
  } catch (e) {
    [pathname] = mediaURL.split(/[?#]/);
  }
  const lastSegment = pathname.split('/').pop() || '';
  if (!lastSegment.includes('.')) {
    return '';
  }
  return lastSegment.split('.').pop().toLowerCase();
};

// Normalize known hosted-player URLs to an embeddable iframe src. Handles the
// common share/watch URL shapes as well as URLs that are already embed URLs.
// Returns null when the URL isn't a recognized provider.
const getEmbedUrl = (mediaURL) => {
  let url;
  try {
    url = new URL(mediaURL, window.location.origin);
  } catch (e) {
    return null;
  }
  const host = url.hostname.replace(/^www\./, '').toLowerCase();

  // Vimeo: player.vimeo.com/video/<id> (already embeddable, keep query such as
  // the unlisted-video hash `?h=`), or vimeo.com/<id>.
  if (host === 'player.vimeo.com') {
    return url.href;
  }
  if (host === 'vimeo.com') {
    const id = url.pathname.split('/').filter(Boolean)[0];
    return id ? `https://player.vimeo.com/video/${id}${url.search}` : null;
  }

  // YouTube: youtu.be/<id>, youtube.com/watch?v=<id>, youtube.com/embed/<id>,
  // youtube.com/live/<id>, youtube.com/shorts/<id>.
  if (host === 'youtu.be') {
    const id = url.pathname.split('/').filter(Boolean)[0];
    return id ? `https://www.youtube.com/embed/${id}` : null;
  }
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    if (url.pathname === '/watch') {
      const id = url.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    const [prefix, id] = url.pathname.split('/').filter(Boolean);
    if (['embed', 'live', 'shorts', 'v'].includes(prefix) && id) {
      return `https://www.youtube.com/embed/${id}`;
    }
  }

  return null;
};

const renderIframe = (src, isPopUp, isMobile) => {
  const width = isPopUp ? (isMobile ? 260 : 727) : '100%';
  const height = isPopUp ? (isMobile ? 146 : 409) : 315;
  return (
    <iframe
      title="Webcam from this platform"
      src={src}
      width={width}
      height={height}
      style={{ border: 0, maxWidth: '100%' }}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  );
};

export const videoOrImage = (mediaURL, isPopUp, isMobile = true) => {
  if (!mediaURL) {
    return '';
  }

  // Hosted players (Vimeo, YouTube, ...) don't expose a file extension and must
  // be embedded via an iframe rather than handed to video.js.
  const embedUrl = getEmbedUrl(mediaURL);
  if (embedUrl) {
    return renderIframe(embedUrl, isPopUp, isMobile);
  }

  const urlExt = getUrlExtension(mediaURL);

  if (IMAGE_EXTENSIONS.includes(urlExt)) {
    if (isPopUp) {
      return (
        <React.Fragment>
          <img src={mediaURL} width={isMobile ? 260 : 727} alt="Webcam from this platform" />
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <img src={mediaURL} alt="Webcam from this platform" />
      </React.Fragment>
    );
  }

  if (VIDEO_FILE_EXTENSIONS.includes(urlExt)) {
    const type = urlExt === 'm3u8' ? 'application/x-mpegURL' : 'video/mp4';
    return (
      <VideoJS options={videoJsOptions(mediaURL, type)} />
    );
  }

  // No recognizable extension and not a known provider: treat it as an
  // embeddable page (e.g. a partner-hosted webcam viewer) rather than assuming
  // a raw mp4 stream.
  return renderIframe(mediaURL, isPopUp, isMobile);
};
